import { Link } from "@inertiajs/react"
import { Search } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useDebounce } from "@/hooks/use-debounce"
import { formatRelativeTimeLong } from "@/lib/utils"
import { askConversationPath, chatsPath } from "@/routes"
import type { ChatSummary } from "@/types"

interface ChatHistorySheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ChatsResponse {
  chats: ChatSummary[]
  total_count: number
  search: string
  page: number
}

function formatExactDate(dateString: string): string {
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  })
}

export function ChatHistorySheet({
  open,
  onOpenChange,
}: ChatHistorySheetProps) {
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebounce(searchInput, 300)
  const [chats, setChats] = useState<ChatSummary[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const fetchChats = useCallback(async (search: string, pageNum: number) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (search) params.set("search", search)
      if (pageNum > 1) params.set("page", String(pageNum))
      const url = `${chatsPath()}.json${params.toString() ? `?${params}` : ""}`
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      const data = (await response.json()) as ChatsResponse
      return data
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    void fetchChats(debouncedSearch, page).then((data) => {
      if (cancelled) return
      setChats(data.chats)
      setTotalCount(data.total_count)
    })
    return () => {
      cancelled = true
    }
  }, [open, debouncedSearch, page, fetchChats])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    setPage(1)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSearchInput("")
      setPage(1)
    }
    onOpenChange(next)
  }

  const hasMore = chats.length < totalCount

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex flex-col gap-0 sm:max-w-md">
        <SheetHeader className="border-b">
          <SheetTitle>Chat history</SheetTitle>
        </SheetHeader>

        <div className="border-b p-4">
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search titles and messages"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && chats.length === 0 ? (
            <p className="text-muted-foreground py-12 text-center text-sm">
              Loading…
            </p>
          ) : chats.length === 0 ? (
            <p className="text-muted-foreground py-12 text-center text-sm">
              {debouncedSearch
                ? "No chats match your search."
                : "No chats yet."}
            </p>
          ) : (
            <>
              <p className="text-muted-foreground mb-3 text-xs">
                {debouncedSearch
                  ? `${totalCount} chat${totalCount !== 1 ? "s" : ""} matching "${debouncedSearch}"`
                  : `${totalCount} chat${totalCount !== 1 ? "s" : ""} with Imperative`}
              </p>
              <div className="divide-y">
                {chats.map((chat) => (
                  <Link
                    key={chat.uid}
                    href={askConversationPath(chat.uid)}
                    onClick={() => onOpenChange(false)}
                    className="hover:bg-muted/50 block px-2 py-3 transition-colors"
                  >
                    <p className="truncate text-sm font-medium">
                      {chat.title ?? "New conversation"}
                    </p>
                    {chat.match_context && (
                      <p className="text-muted-foreground mt-0.5 truncate text-xs italic">
                        &ldquo;{chat.match_context}&rdquo;
                      </p>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-muted-foreground text-xs">
                          Last message {formatRelativeTimeLong(chat.updated_at)}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent>
                        {formatExactDate(chat.updated_at)}
                      </TooltipContent>
                    </Tooltip>
                  </Link>
                ))}
              </div>
              {hasMore && (
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={loading}
                  >
                    Show more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
